
const GlobalReachSection = () => {
  return (
    <section className="py-32 bg-gradient-to-b from-slate-800/30 to-slate-900/30 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-cyan-600/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-6 max-w-7xl">
        <div className="text-center mb-20">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-blue-600/20 text-blue-300 rounded-full text-sm font-medium border border-blue-400/30">
              Global Presence
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight">
            Connecting <strong className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Continents</strong>
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto font-light leading-relaxed">
            Strategically positioned in Hong Kong, we serve as the vital link between East and West, 
            facilitating seamless technology transfer across global markets
          </p>
        </div>

        <div className="relative">
          {/* World Map Container */}
          <div className="relative bg-white/5 backdrop-blur-md border border-white/20 rounded-3xl p-12 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-cyan-600/5"></div>
            
            {/* World Map */}
            <div className="relative w-full h-96 flex items-center justify-center">
              <div className="w-full max-w-5xl h-full relative">
                {/* World Map Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-30 rounded-2xl"
                  style={{
                    backgroundImage: `url('https://public.readdy.ai/ai/img_res/6a28b8f9a14c7d528f3d5ee839d0d280.jpg')`
                  }}
                ></div>

                {/* Overlay grid pattern */}
                <div className="absolute inset-0 opacity-20">
                  <div className="w-full h-full" style={{
                    backgroundImage: `
                      linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: '40px 40px'
                  }}></div>
                </div>

                {/* Connection Lines from Hong Kong */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  {/* Line to Europe */}
                  <line 
                    x1="65%" y1="45%" 
                    x2="35%" y2="30%" 
                    stroke="url(#gradient1)" 
                    strokeWidth="2" 
                    strokeDasharray="5,5"
                    className="animate-pulse"
                  />
                  {/* Line to North America */}
                  <line 
                    x1="65%" y1="45%" 
                    x2="20%" y2="35%" 
                    stroke="url(#gradient2)" 
                    strokeWidth="2" 
                    strokeDasharray="5,5"
                    className="animate-pulse"
                  />
                  {/* Line to Asia Pacific */}
                  <line 
                    x1="65%" y1="45%" 
                    x2="75%" y2="65%" 
                    stroke="url(#gradient3)" 
                    strokeWidth="2" 
                    strokeDasharray="5,5"
                    className="animate-pulse"
                  />
                  
                  <defs>
                    <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8"/>
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.3"/>
                    </linearGradient>
                    <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8"/>
                      <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.3"/>
                    </linearGradient>
                    <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8"/>
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0.3"/>
                    </linearGradient>
                  </defs>
                </svg>

                {/* Hong Kong - Central Hub */}
                <div className="absolute" style={{ top: '45%', left: '65%', transform: 'translate(-50%, -50%)' }}>
                  <div className="relative group cursor-pointer">
                    <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 rounded-full border-4 border-white shadow-2xl relative z-10">
                      <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-75"></div>
                    </div>
                    <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg">
                      🏢 Hong Kong HQ
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-500"></div>
                    </div>
                  </div>
                </div>

                {/* Europe */}
                <div className="absolute" style={{ top: '30%', left: '35%', transform: 'translate(-50%, -50%)' }}>
                  <div className="relative group cursor-pointer">
                    <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full border-3 border-white shadow-xl hover:scale-110 transition-transform duration-300"></div>
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg">
                      🇪🇺 Europe
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-blue-500"></div>
                    </div>
                  </div>
                </div>

                {/* North America */}
                <div className="absolute" style={{ top: '35%', left: '20%', transform: 'translate(-50%, -50%)' }}>
                  <div className="relative group cursor-pointer">
                    <div className="w-6 h-6 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full border-3 border-white shadow-xl hover:scale-110 transition-transform duration-300"></div>
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-cyan-500 text-white px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg">
                      🇺🇸 North America
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-cyan-500"></div>
                    </div>
                  </div>
                </div>

                {/* Asia Pacific */}
                <div className="absolute" style={{ top: '65%', left: '75%', transform: 'translate(-50%, -50%)' }}>
                  <div className="relative group cursor-pointer">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-green-600 rounded-full border-3 border-white shadow-xl hover:scale-110 transition-transform duration-300"></div>
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg">
                      🌏 Asia Pacific
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-3 border-r-3 border-t-3 border-transparent border-t-green-500"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Regional Information */}
            <div className="grid md:grid-cols-3 gap-8 mt-16">
              <div className="text-center group">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-building-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Europe</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Advanced manufacturing partnerships and cutting-edge R&D collaborations across European markets
                </p>
              </div>

              <div className="text-center group">
                <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-rocket-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">North America</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Innovation hubs and technology transfer initiatives driving next-generation solutions
                </p>
              </div>

              <div className="text-center group">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-earth-line text-xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Asia Pacific</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  Manufacturing excellence and supply chain optimization across diverse Asian markets
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalReachSection;
